import React, { useState, useEffect } from 'react';
import { ArrowLeft, RefreshCw, ExternalLink, ChevronDown, ChevronRight, Cpu, Clock, Zap } from 'lucide-react';
import { authHeaders } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

interface RunPhase {
  phase: string;
  status: string;
  duration_ms?: number;
  model?: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_usd?: number;
  detail?: Record<string, unknown>;
  attempts?: Array<{ status: string; duration_ms: number; detail?: Record<string, unknown> }>;
}

interface ModelCall {
  id: string;
  run_id: string;
  engine: string;
  method: string;
  model: string;
  phase: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens?: number | null;
  cache_write_tokens?: number | null;
  cost_usd: number;
  started_at: string;
  completed_at: string;
  duration_ms?: number | null;
  metadata_json?: Record<string, unknown> | null;
}

interface RunData {
  run_id: string;
  project: string;
  message: string;
  status: string;
  total_duration_ms: number;
  total_cost_usd: number;
  created_at: string;
  completed_at?: string;
  issue_number?: number;
  issue_url?: string;
  pr_number?: number;
  pr_url?: string;
  phases: RunPhase[];
}

interface RunDetailProps {
  runId: string;
  onBack: () => void;
}

type DetailTab = 'phases' | 'calls';

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function formatCost(usd: number): string {
  return `$${usd.toFixed(4)}`;
}

function str(val: unknown): string {
  if (val == null) return '';
  return String(val);
}

function rec(val: unknown): Record<string, unknown> {
  if (val && typeof val === 'object' && !Array.isArray(val)) return val as Record<string, unknown>;
  return {};
}

const PHASE_LABELS: Record<string, string> = {
  classify: 'Classify',
  discover: 'Discover',
  issue: 'Issue Created',
  spec: 'Spec',
  implement: 'Implement',
  ci: 'CI',
  review: 'Review',
  merge: 'Merge',
  pr: 'Pull Request',
  human_gate: 'Human Gate',
  deploy: 'Deploy',
};

// R-2: Color-coded confidence score
function confidenceColor(confidence: number): string {
  if (confidence >= 0.85) return 'var(--green)';
  if (confidence >= 0.6) return 'var(--orange)';
  return 'var(--red)';
}

function statusColor(s: string): string {
  if (s === 'complete' || s === 'success') return 'var(--green)';
  if (s === 'failed') return 'var(--red)';
  if (s === 'in_progress' || s === 'running') return 'var(--blue)';
  if (s === 'skipped') return 'var(--text-muted)';
  if (s === 'waiting') return 'var(--yellow)';
  return 'var(--text-muted)';
}

function statusBg(s: string): string {
  if (s === 'complete' || s === 'success') return 'var(--green-dim)';
  if (s === 'failed') return 'var(--red-dim)';
  if (s === 'in_progress' || s === 'running') return 'var(--blue-dim)';
  return 'var(--surface3)';
}

export const RunDetail: React.FC<RunDetailProps> = ({ runId, onBack }) => {
  const [run, setRun] = useState<RunData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);
  const [expandedAttempts, setExpandedAttempts] = useState<Record<string, boolean>>({});
  const [detailTab, setDetailTab] = useState<DetailTab>('phases');
  const [modelCalls, setModelCalls] = useState<ModelCall[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [expandedCalls, setExpandedCalls] = useState<Record<string, boolean>>({});

  const fetchRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = await authHeaders();
      const res = await fetch(`${API_BASE}/api/runs/${runId}`, { headers: auth });
      if (!res.ok) throw new Error(`Failed to fetch run: ${res.status}`);
      const data = await res.json();
      setRun(data);
      if (!selectedPhase && data.phases?.length > 0) {
        setSelectedPhase(data.phases[0].phase);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load run');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalls = async () => {
    setCallsLoading(true);
    try {
      const auth = await authHeaders();
      const res = await fetch(`${API_BASE}/api/runs/${runId}/calls`, { headers: auth });
      if (!res.ok) throw new Error(`Failed to fetch calls: ${res.status}`);
      const data = await res.json();
      setModelCalls(data.calls || []);
    } catch {
      // Silently fail — calls panel will show empty state
      setModelCalls([]);
    } finally {
      setCallsLoading(false);
    }
  };

  useEffect(() => {
    fetchRun();
    fetchCalls();
    const interval = setInterval(() => {
      if (run?.status === 'in_progress') {
        fetchRun();
        fetchCalls();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRerun = async () => {
    try {
      const auth = await authHeaders();
      await fetch(`${API_BASE}/api/runs/${runId}/rerun`, { method: 'POST', headers: auth });
      fetchRun();
    } catch { /* ignore */ }
  };

  if (loading && !run) {
    return (
      <div className="empty-state" style={{ height: 256 }}>
        <div className="spinner" />
        <span className="muted">Loading run...</span>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div>
        <button className="btn btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>
          <ArrowLeft size={12} /> Back
        </button>
        <div className="empty-state">
          <div className="empty-state-title">{error || 'Run not found'}</div>
          <button className="btn btn-sm" onClick={fetchRun}>Retry</button>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 12 }}>
            Encountering a persistent issue?{' '}
            <button
              onClick={() => (window as any).LumenIQSupport?.open()}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', textDecoration: 'underline',
                fontSize: 'var(--text-xs)', fontFamily: 'inherit', padding: 0,
              }}
            >
              Contact support
            </button>
            {' '}&mdash; include your pipeline ID and error message for fastest help.
          </p>
        </div>
      </div>
    );
  }

  const selectedPhaseData = run.phases.find(p => p.phase === selectedPhase);

  // Cost breakdown by model
  const costByModel: Record<string, { tokens: number; cost: number }> = {};
  for (const phase of run.phases) {
    if (phase.model && phase.cost_usd) {
      if (!costByModel[phase.model]) costByModel[phase.model] = { tokens: 0, cost: 0 };
      costByModel[phase.model].tokens += (phase.tokens_in || 0) + (phase.tokens_out || 0);
      costByModel[phase.model].cost += phase.cost_usd;
    }
  }

  // Group model calls by phase
  const callsByPhase: Record<string, ModelCall[]> = {};
  for (const call of modelCalls) {
    if (!callsByPhase[call.phase]) callsByPhase[call.phase] = [];
    callsByPhase[call.phase].push(call);
  }

  // Filter calls for selected phase
  const selectedPhaseCalls = selectedPhase ? (callsByPhase[selectedPhase] || []) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div className="page-title-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-sm" onClick={onBack} style={{ padding: 6 }}>
            <ArrowLeft size={14} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 className="page-title" style={{ marginBottom: 0 }}>Run {runId.slice(0, 8)}</h2>
              <span className="badge badge-library">{run.project}</span>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: statusColor(run.status),
                display: 'inline-block',
                boxShadow: `0 0 6px ${statusColor(run.status)}40`,
              }} />
            </div>
            <div className="mono muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>
              {new Date(run.created_at).toLocaleString()} · {formatDuration(run.total_duration_ms)}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={fetchRun} title="Refresh">
            <RefreshCw size={11} />
          </button>
          {(run.status === 'failed' || run.status === 'complete') && (
            <button className="btn btn-sm" onClick={handleRerun}>Re-run</button>
          )}
          {run.status === 'failed' && (
            <button
              className="btn btn-sm"
              onClick={() => (window as any).LumenIQSupport?.open()}
              style={{ background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid var(--red)' }}
            >
              Report this failure
            </button>
          )}
        </div>
      </div>

      {/* USS support prompt on failure */}
      {run.status === 'failed' && (
        <div style={{
          padding: '8px 14px', fontSize: 'var(--text-xs)', color: 'var(--text-muted)',
          fontFamily: 'var(--font-mono)', background: 'var(--surface2)',
          borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
        }}>
          Pipeline failed — include the run ID <code style={{ color: 'var(--text-dim)' }}>{run.run_id}</code> and the error message shown below when reporting.
        </div>
      )}

      {/* Original request */}
      <div className="card" style={{ padding: '10px 14px' }}>
        <div className="section-label" style={{ marginBottom: 4 }}>Original request</div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)', fontStyle: 'italic' }}>
          &ldquo;{run.message}&rdquo;
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
        {/* Timeline (left) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {run.phases.map((phase) => (
            <button
              key={phase.phase}
              onClick={() => setSelectedPhase(phase.phase)}
              style={{
                width: '100%', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 'var(--radius-md)',
                background: selectedPhase === phase.phase ? 'var(--surface2)' : 'transparent',
                border: selectedPhase === phase.phase ? '1px solid var(--border)' : '1px solid transparent',
                cursor: 'pointer', transition: 'all var(--transition-fast)',
                color: 'inherit', fontFamily: 'inherit',
              }}
              onMouseEnter={e => { if (selectedPhase !== phase.phase) e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { if (selectedPhase !== phase.phase) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: statusBg(phase.status), color: statusColor(phase.status),
                fontSize: 9,
              }}>
                {(phase.status === 'in_progress' || phase.status === 'running')
                  ? <div className="spinner" style={{ width: 10, height: 10, borderWidth: 1.5 }} />
                  : (phase.status === 'complete' || phase.status === 'success') ? '✓'
                  : phase.status === 'failed' ? '✕'
                  : phase.status === 'skipped' ? '–' : '·'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
                  {PHASE_LABELS[phase.phase] || phase.phase}
                </div>
                {phase.attempts && phase.attempts.length > 1 && (
                  <div className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>(attempt {phase.attempts.length})</div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {callsByPhase[phase.phase] && callsByPhase[phase.phase].length > 0 && (
                  <span className="mono" style={{
                    fontSize: 9, color: 'var(--text-muted)',
                    background: 'var(--surface3)', padding: '1px 4px',
                    borderRadius: 'var(--radius-sm)',
                  }}>
                    {callsByPhase[phase.phase].length} call{callsByPhase[phase.phase].length !== 1 ? 's' : ''}
                  </span>
                )}
                <span className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>
                  {phase.duration_ms ? formatDuration(phase.duration_ms) : '—'}
                </span>
              </div>
            </button>
          ))}

          {/* Cost summary */}
          {Object.keys(costByModel).length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div className="section-label">Cost Breakdown</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.entries(costByModel).map(([model, data]) => (
                  <div key={model} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)' }}>
                    <span className="mono dim">{model}</span>
                    <div>
                      <span className="mono muted">{data.tokens.toLocaleString()} tok</span>
                      <span className="mono" style={{ color: 'var(--text)', marginLeft: 8 }}>{formatCost(data.cost)}</span>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--text)' }}>Total</span>
                  <span className="mono" style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--orange)' }}>{formatCost(run.total_cost_usd)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Model calls summary */}
          {modelCalls.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <div className="section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Cpu size={11} />
                Model Calls
              </div>
              <div className="mono muted" style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>
                {modelCalls.length} total call{modelCalls.length !== 1 ? 's' : ''} ·{' '}
                {modelCalls.reduce((s, c) => s + c.input_tokens + c.output_tokens, 0).toLocaleString()} tokens ·{' '}
                {formatCost(modelCalls.reduce((s, c) => s + c.cost_usd, 0))}
              </div>
            </div>
          )}

          {/* Links */}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {run.issue_url && (
              <a href={run.issue_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none' }}>
                Issue #{run.issue_number} <ExternalLink size={10} />
              </a>
            )}
            {run.pr_url && (
              <a href={run.pr_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none' }}>
                PR #{run.pr_number} <ExternalLink size={10} />
              </a>
            )}
          </div>
        </div>

        {/* Detail panel (right) */}
        <div>
          {/* Tab switcher */}
          <div style={{
            display: 'flex', gap: 0, marginBottom: 12,
            borderBottom: '1px solid var(--border)',
          }}>
            <button
              onClick={() => setDetailTab('phases')}
              style={{
                padding: '8px 16px', fontSize: 'var(--text-xs)',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: detailTab === 'phases' ? 'var(--text)' : 'var(--text-muted)',
                borderBottom: detailTab === 'phases' ? '2px solid var(--orange)' : '2px solid transparent',
                fontFamily: 'inherit', fontWeight: detailTab === 'phases' ? 'var(--weight-semibold)' : 'var(--weight-normal)',
                transition: 'all var(--transition-fast)',
              }}
            >
              Phase Detail
            </button>
            <button
              onClick={() => setDetailTab('calls')}
              style={{
                padding: '8px 16px', fontSize: 'var(--text-xs)',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: detailTab === 'calls' ? 'var(--text)' : 'var(--text-muted)',
                borderBottom: detailTab === 'calls' ? '2px solid var(--orange)' : '2px solid transparent',
                fontFamily: 'inherit', fontWeight: detailTab === 'calls' ? 'var(--weight-semibold)' : 'var(--weight-normal)',
                transition: 'all var(--transition-fast)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <Cpu size={11} />
              Model Calls
              {selectedPhaseCalls.length > 0 && (
                <span style={{
                  background: 'var(--surface3)', padding: '1px 6px',
                  borderRadius: 'var(--radius-sm)', fontSize: 9,
                  color: 'var(--text-muted)',
                }}>
                  {selectedPhaseCalls.length}
                </span>
              )}
            </button>
          </div>

          {detailTab === 'phases' && (
            selectedPhaseData ? (
              <PhaseDetailPanel phase={selectedPhaseData} expandedAttempts={expandedAttempts} setExpandedAttempts={setExpandedAttempts} />
            ) : (
              <div className="empty-state" style={{ height: 200 }}>
                <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>Select a phase to view details</span>
              </div>
            )
          )}

          {detailTab === 'calls' && (
            <ModelCallsPanel
              calls={selectedPhaseCalls}
              allCalls={modelCalls}
              selectedPhase={selectedPhase}
              loading={callsLoading}
              expandedCalls={expandedCalls}
              setExpandedCalls={setExpandedCalls}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Model Calls Panel ─────────────────────────────── */

interface ModelCallsPanelProps {
  calls: ModelCall[];
  allCalls: ModelCall[];
  selectedPhase: string | null;
  loading: boolean;
  expandedCalls: Record<string, boolean>;
  setExpandedCalls: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const ModelCallsPanel: React.FC<ModelCallsPanelProps> = ({
  calls, allCalls, selectedPhase, loading, expandedCalls, setExpandedCalls,
}) => {
  if (loading) {
    return (
      <div className="empty-state" style={{ height: 200 }}>
        <div className="spinner" />
        <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>Loading model calls...</span>
      </div>
    );
  }

  if (allCalls.length === 0) {
    return (
      <div className="card" style={{ padding: '24px 14px', textAlign: 'center' }}>
        <Cpu size={24} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} />
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          No model calls recorded for this run
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 4 }}>
          Model call logging may not be enabled, or the run has not yet made any LLM calls.
        </div>
      </div>
    );
  }

  const displayCalls = calls.length > 0 ? calls : allCalls;
  const showingAll = calls.length === 0;

  // Summary stats for displayed calls
  const totalTokensIn = displayCalls.reduce((s, c) => s + c.input_tokens, 0);
  const totalTokensOut = displayCalls.reduce((s, c) => s + c.output_tokens, 0);
  const totalCost = displayCalls.reduce((s, c) => s + c.cost_usd, 0);
  const totalDuration = displayCalls.reduce((s, c) => s + (c.duration_ms || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Summary bar */}
      <div className="card" style={{
        padding: '10px 14px', display: 'flex', gap: 16,
        alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {showingAll ? 'All phases' : (PHASE_LABELS[selectedPhase || ''] || selectedPhase)}
          {' — '}
          <span style={{ color: 'var(--text-dim)' }}>{displayCalls.length} call{displayCalls.length !== 1 ? 's' : ''}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, marginLeft: 'auto', fontSize: 'var(--text-xs)' }}>
          <span className="mono muted">
            <Zap size={9} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />
            {totalTokensIn.toLocaleString()} in / {totalTokensOut.toLocaleString()} out
          </span>
          <span className="mono muted">
            <Clock size={9} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />
            {formatDuration(totalDuration)}
          </span>
          <span className="mono" style={{ color: 'var(--orange)' }}>
            {formatCost(totalCost)}
          </span>
        </div>
      </div>

      {/* Individual calls */}
      {displayCalls.map((call, i) => {
        const expanded = expandedCalls[call.id];
        const dur = call.duration_ms || (new Date(call.completed_at).getTime() - new Date(call.started_at).getTime());
        const meta = call.metadata_json || {};
        const hasExpandableContent = Object.keys(meta).length > 0;

        return (
          <div key={call.id} className="card" style={{ overflow: 'hidden', padding: 0 }}>
            {/* Call header — always visible */}
            <button
              onClick={() => {
                if (hasExpandableContent) {
                  setExpandedCalls(prev => ({ ...prev, [call.id]: !expanded }));
                }
              }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', textAlign: 'left',
                cursor: hasExpandableContent ? 'pointer' : 'default',
                background: 'transparent', border: 'none', color: 'inherit', fontFamily: 'inherit',
                transition: 'background var(--transition-fast)',
              }}
              onMouseEnter={e => { if (hasExpandableContent) e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Expand chevron */}
              <div style={{ width: 14, flexShrink: 0 }}>
                {hasExpandableContent && (
                  expanded
                    ? <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} />
                    : <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />
                )}
              </div>

              {/* Call number */}
              <span className="mono" style={{
                fontSize: 9, color: 'var(--text-muted)',
                background: 'var(--surface3)', padding: '2px 5px',
                borderRadius: 'var(--radius-sm)', flexShrink: 0,
              }}>
                #{i + 1}
              </span>

              {/* Engine + model */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', fontWeight: 'var(--weight-semibold)' }}>
                    {call.engine}
                  </span>
                  <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    {call.model}
                  </span>
                </div>
                <div className="mono muted" style={{ fontSize: 9, marginTop: 2 }}>
                  {call.method}
                  {showingAll && (
                    <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>
                      [{PHASE_LABELS[call.phase] || call.phase}]
                    </span>
                  )}
                </div>
              </div>

              {/* Token counts */}
              <div className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>
                <div>
                  <span style={{ color: 'var(--green)' }}>{call.input_tokens.toLocaleString()}</span>
                  {' / '}
                  <span style={{ color: 'var(--blue)' }}>{call.output_tokens.toLocaleString()}</span>
                  {' tok'}
                </div>
                {(call.cache_read_tokens || call.cache_write_tokens) && (
                  <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>
                    cache: {(call.cache_read_tokens || 0).toLocaleString()}r / {(call.cache_write_tokens || 0).toLocaleString()}w
                  </div>
                )}
              </div>

              {/* Duration */}
              <span className="mono muted" style={{ fontSize: 'var(--text-xs)', flexShrink: 0, width: 50, textAlign: 'right' }}>
                {formatDuration(dur)}
              </span>

              {/* Cost */}
              <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--orange)', flexShrink: 0, width: 55, textAlign: 'right' }}>
                {formatCost(call.cost_usd)}
              </span>
            </button>

            {/* Expanded metadata */}
            {expanded && hasExpandableContent && (
              <div style={{ borderTop: '1px solid var(--border)', padding: '10px 14px' }}>
                <ModelCallMetadata metadata={meta} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── Model Call Metadata Renderer ──────────────────── */

const ModelCallMetadata: React.FC<{ metadata: Record<string, unknown> }> = ({ metadata }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Try to extract known fields
  const prompt = metadata.prompt || metadata.input || metadata.system_prompt || metadata.messages;
  const response = metadata.response || metadata.output || metadata.completion || metadata.result;
  const systemPrompt = metadata.system_prompt || metadata.system;
  const messages = metadata.messages;

  // Render a text section with expand/collapse for long content
  const renderTextSection = (label: string, content: unknown, key: string) => {
    if (content == null) return null;
    const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    const isLong = text.length > 500;
    const expanded = expandedSections[key];
    const displayText = isLong && !expanded ? text.slice(0, 500) + '...' : text;

    return (
      <div key={key} style={{ marginBottom: 10 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 4,
        }}>
          <span className="section-label" style={{ margin: 0 }}>{label}</span>
          {isLong && (
            <button
              onClick={() => setExpandedSections(prev => ({ ...prev, [key]: !expanded }))}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--orange)', fontSize: 'var(--text-xs)',
                fontFamily: 'inherit', padding: '2px 4px',
              }}
            >
              {expanded ? 'Collapse' : `Show all (${text.length.toLocaleString()} chars)`}
            </button>
          )}
        </div>
        <div className="mono" style={{
          padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-xs)', color: 'var(--text-dim)', lineHeight: 1.6,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          maxHeight: expanded ? 'none' : 300, overflowY: expanded ? 'visible' : 'auto',
        }}>
          {displayText}
        </div>
      </div>
    );
  };

  // If we have known fields, render them structured
  if (prompt || response || systemPrompt || messages) {
    return (
      <div>
        {systemPrompt && !messages ? renderTextSection('System Prompt', systemPrompt, 'system') : null}
        {messages ? renderTextSection('Messages', messages, 'messages') : null}
        {prompt && !messages && !systemPrompt ? renderTextSection('Input', prompt, 'prompt') : null}
        {response ? renderTextSection('Response', response, 'response') : null}
        {/* Render any remaining keys */}
        {Object.entries(metadata)
          .filter(([k]) => !['prompt', 'input', 'system_prompt', 'system', 'messages', 'response', 'output', 'completion', 'result'].includes(k))
          .map(([k, v]) => renderTextSection(k, v, k))
        }
      </div>
    );
  }

  // Fallback: render all metadata as JSON
  return (
    <div className="mono" style={{
      padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
      fontSize: 'var(--text-xs)', color: 'var(--text-muted)', whiteSpace: 'pre-wrap',
      maxHeight: 400, overflowY: 'auto',
    }}>
      {JSON.stringify(metadata, null, 2)}
    </div>
  );
};

/* ── Phase Detail Panel ──────────────────────────── */

interface PhaseDetailPanelProps {
  phase: RunPhase;
  expandedAttempts: Record<string, boolean>;
  setExpandedAttempts: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const PhaseDetailPanel: React.FC<PhaseDetailPanelProps> = ({ phase, expandedAttempts, setExpandedAttempts }) => {
  const detail: Record<string, unknown> = phase.detail || {};

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {/* Phase header */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)', color: 'var(--text)' }}>
            {PHASE_LABELS[phase.phase] || phase.phase}
          </span>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: statusColor(phase.status),
            display: 'inline-block',
          }} />
        </div>
        {phase.model && (
          <div className="mono muted" style={{ fontSize: 'var(--text-xs)', marginTop: 6, display: 'flex', gap: 14 }}>
            <span>Model: <span style={{ color: 'var(--text-dim)' }}>{phase.model}</span></span>
            {phase.tokens_in != null && (
              <span>Tokens: <span style={{ color: 'var(--text-dim)' }}>{phase.tokens_in?.toLocaleString()} in / {phase.tokens_out?.toLocaleString()} out</span></span>
            )}
            {phase.duration_ms != null && (
              <span>Duration: <span style={{ color: 'var(--text-dim)' }}>{formatDuration(phase.duration_ms)}</span></span>
            )}
            {phase.cost_usd != null && (
              <span>Cost: <span style={{ color: 'var(--orange)' }}>{formatCost(phase.cost_usd)}</span></span>
            )}
          </div>
        )}
      </div>

      {/* Phase content */}
      <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Classify */}
        {phase.phase === 'classify' && detail.classification != null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <DetailRow label="Type" value={str(rec(detail.classification).type)} />
            <DetailRow label="Risk" value={str(rec(detail.classification).risk)} />
            <DetailRow label="Route" value={str(rec(detail.classification).route)} />
            <DetailRow label="Confidence" value={str(rec(detail.classification).confidence)} />
            {rec(detail.classification).confidence != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 8 }}>
                <div style={{
                  width: 60, height: 6, borderRadius: 3, background: 'var(--surface3)', overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${Math.min(100, Number(rec(detail.classification).confidence) * 100)}%`,
                    height: '100%', borderRadius: 3,
                    background: confidenceColor(Number(rec(detail.classification).confidence)),
                  }} />
                </div>
                <span className="mono" style={{
                  fontSize: 'var(--text-xs)',
                  color: confidenceColor(Number(rec(detail.classification).confidence)),
                  fontWeight: 600,
                }}>
                  {(Number(rec(detail.classification).confidence) * 100).toFixed(0)}%
                </span>
              </div>
            )}
            {rec(detail.classification).reasoning != null && (
              <div className="mono" style={{
                marginTop: 4, padding: 10, background: 'var(--surface2)',
                borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)', lineHeight: 1.6,
              }}>
                {str(rec(detail.classification).reasoning)}
              </div>
            )}
          </div>
        )}

        {/* Discover (R-2) */}
        {phase.phase === 'discover' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.affected_files != null && Array.isArray(detail.affected_files) && (
              <div>
                <div className="section-label">Affected Files ({(detail.affected_files as string[]).length})</div>
                {(detail.affected_files as string[]).map((f, i) => (
                  <div key={i} className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', paddingLeft: 8, lineHeight: 1.8 }}>
                    {f}
                  </div>
                ))}
              </div>
            )}
            {detail.repo_file_count != null && <DetailRow label="Repo files" value={str(detail.repo_file_count)} />}
            {detail.repo_line_count != null && <DetailRow label="Total lines" value={str(detail.repo_line_count)} />}
            {detail.questions != null && Array.isArray(detail.questions) && (
              <div>
                <div className="section-label">Discovery Questions</div>
                {(detail.questions as Array<{ question: string; answer?: string }>).map((q, i) => (
                  <div key={i} style={{ paddingLeft: 8, marginBottom: 6 }}>
                    <div className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>Q: {typeof q === 'string' ? q : q.question}</div>
                    {typeof q !== 'string' && q.answer && (
                      <div className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--green)', paddingLeft: 12 }}>A: {q.answer}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* Fallback: render any remaining detail fields */}
            {!detail.affected_files && !detail.questions && Object.keys(detail).length > 0 && (
              <div className="mono" style={{
                padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-xs)', color: 'var(--text-muted)', whiteSpace: 'pre-wrap',
              }}>
                {JSON.stringify(detail, null, 2)}
              </div>
            )}
          </div>
        )}

        {/* Issue */}
        {phase.phase === 'issue' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.issue_number != null && <DetailRow label="Issue" value={`#${str(detail.issue_number)}`} />}
            {detail.title != null && <DetailRow label="Title" value={str(detail.title)} />}
            {detail.issue_url != null && (
              <a href={str(detail.issue_url)} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none' }}>
                View on GitHub <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}

        {/* Spec */}
        {phase.phase === 'spec' && detail.spec != null && (
          <div className="mono" style={{
            padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-xs)', color: 'var(--text-dim)', lineHeight: 1.6, whiteSpace: 'pre-wrap',
          }}>
            {str(detail.spec)}
          </div>
        )}

        {/* Implement */}
        {phase.phase === 'implement' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.commit_sha != null && <DetailRow label="Commit" value={str(detail.commit_sha).slice(0, 7)} />}
            {detail.files_changed != null && Array.isArray(detail.files_changed) && (
              <div>
                <div className="section-label">Files changed</div>
                {(detail.files_changed as Array<{ file: string; additions: number; deletions: number }>).map((f, i) => (
                  <div key={i} className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', paddingLeft: 8, lineHeight: 1.8 }}>
                    {f.file} <span style={{ color: 'var(--green)' }}>+{f.additions}</span> <span style={{ color: 'var(--red)' }}>-{f.deletions}</span>
                  </div>
                ))}
              </div>
            )}
            {detail.commit_url != null && (
              <a href={str(detail.commit_url)} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none' }}>
                View commit <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}

        {/* CI with attempts */}
        {phase.phase === 'ci' && phase.attempts && phase.attempts.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {phase.attempts.map((attempt, i) => {
              const key = `ci_${i}`;
              const expanded = expandedAttempts[key];
              return (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                  <button
                    onClick={() => setExpandedAttempts(prev => ({ ...prev, [key]: !expanded }))}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px', textAlign: 'left', cursor: 'pointer',
                      background: 'transparent', border: 'none', color: 'inherit', fontFamily: 'inherit',
                      transition: 'background var(--transition-fast)',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {expanded ? <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />}
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>Attempt {i + 1}</span>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor(attempt.status) }} />
                    <span className="mono muted" style={{ fontSize: 'var(--text-xs)', marginLeft: 'auto' }}>{formatDuration(attempt.duration_ms)}</span>
                  </button>
                  {expanded && attempt.detail && (
                    <div className="mono" style={{
                      padding: '8px 10px', fontSize: 'var(--text-xs)',
                      color: 'var(--text-muted)', whiteSpace: 'pre-wrap',
                      borderTop: '1px solid var(--border)',
                    }}>
                      {typeof attempt.detail === 'string' ? String(attempt.detail) : JSON.stringify(attempt.detail, null, 2)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Review */}
        {phase.phase === 'review' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.decision != null && <DetailRow label="Decision" value={str(detail.decision)} />}
            {detail.comments != null && (
              <div className="mono" style={{
                padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 1.6, whiteSpace: 'pre-wrap',
              }}>
                {str(detail.comments)}
              </div>
            )}
          </div>
        )}

        {/* Merge */}
        {phase.phase === 'merge' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.pr_number != null && <DetailRow label="PR" value={`#${str(detail.pr_number)}`} />}
            {detail.merge_commit != null && <DetailRow label="Merge commit" value={str(detail.merge_commit).slice(0, 7)} />}
            {detail.pr_url != null && (
              <a href={str(detail.pr_url)} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none' }}>
                View PR <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}

        {/* Deploy */}
        {phase.phase === 'deploy' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.deploy_id != null && <DetailRow label="Deploy ID" value={str(detail.deploy_id)} />}
            {detail.service_url != null && <DetailRow label="Service URL" value={str(detail.service_url)} />}
            {detail.build_log != null && (
              <div className="mono" style={{
                padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-xs)', color: 'var(--text-muted)', whiteSpace: 'pre-wrap',
                maxHeight: 200, overflowY: 'auto',
              }}>
                {str(detail.build_log)}
              </div>
            )}
          </div>
        )}

        {/* PR (R-2) */}
        {phase.phase === 'pr' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.pr_number != null && <DetailRow label="PR" value={`#${str(detail.pr_number)}`} />}
            {detail.branch != null && <DetailRow label="Branch" value={str(detail.branch)} />}
            {detail.files_changed != null && <DetailRow label="Files changed" value={str(detail.files_changed)} />}
            {detail.additions != null && <DetailRow label="Additions" value={`+${str(detail.additions)}`} />}
            {detail.deletions != null && <DetailRow label="Deletions" value={`-${str(detail.deletions)}`} />}
            {detail.merge_status != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="section-label" style={{ margin: 0, minWidth: 80 }}>Status</span>
                <span style={{
                  padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)', fontWeight: 600,
                  background: str(detail.merge_status) === 'merged' ? 'var(--green-dim)' : str(detail.merge_status) === 'closed' ? 'var(--red-dim)' : 'var(--blue-dim)',
                  color: str(detail.merge_status) === 'merged' ? 'var(--green)' : str(detail.merge_status) === 'closed' ? 'var(--red)' : 'var(--blue)',
                }}>
                  {str(detail.merge_status)}
                </span>
              </div>
            )}
            {detail.pr_url != null && (
              <a href={str(detail.pr_url)} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--orange)', textDecoration: 'none' }}>
                View PR on GitHub <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}

        {/* Human Gate (R-2) */}
        {phase.phase === 'human_gate' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.approval_status != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="section-label" style={{ margin: 0, minWidth: 80 }}>Status</span>
                <span style={{
                  padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)', fontWeight: 600,
                  background: str(detail.approval_status) === 'approved' ? 'var(--green-dim)' : str(detail.approval_status) === 'rejected' ? 'var(--red-dim)' : 'var(--yellow-dim, var(--surface3))',
                  color: str(detail.approval_status) === 'approved' ? 'var(--green)' : str(detail.approval_status) === 'rejected' ? 'var(--red)' : 'var(--yellow, var(--text-muted))',
                }}>
                  {str(detail.approval_status)}
                </span>
              </div>
            )}
            {detail.approved_by != null && <DetailRow label="Approved by" value={str(detail.approved_by)} />}
            {detail.approved_at != null && <DetailRow label="Timestamp" value={new Date(str(detail.approved_at)).toLocaleString()} />}
            {detail.reason != null && (
              <div className="mono" style={{
                padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 1.6, whiteSpace: 'pre-wrap',
              }}>
                {str(detail.reason)}
              </div>
            )}
          </div>
        )}

        {/* Generic fallback — R-2: covers unknown phase types */}
        {!['classify', 'discover', 'issue', 'spec', 'implement', 'ci', 'review', 'merge', 'pr', 'human_gate', 'deploy'].includes(phase.phase) && (
          <div className="mono" style={{
            padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-xs)', color: 'var(--text-muted)', whiteSpace: 'pre-wrap',
          }}>
            {JSON.stringify(detail, null, 2)}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Helper component ────────────────────────────── */

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 'var(--text-xs)' }}>
    <span className="muted" style={{ width: 80, flexShrink: 0 }}>{label}:</span>
    <span style={{ color: 'var(--text-dim)' }}>{value}</span>
  </div>
);
